export const CARD_BRANDS = ['Visa', 'Mastercard', 'American Express', 'Discover']

export function normalizeCardLast4(value) {
  return String(value || '').replace(/\D/g, '').slice(0, 4)
}

export function validatePaymentMethodInput({ brand, last4 }, existingMethods = []) {
  const cleanBrand = CARD_BRANDS.includes(brand) ? brand : CARD_BRANDS[0]
  const cleanLast4 = normalizeCardLast4(last4)

  if (!/^\d{4}$/.test(cleanLast4)) {
    return {
      ok: false,
      error: 'Enter the last 4 digits of the card.',
      payload: null,
    }
  }

  const duplicate = existingMethods.some((method) => method.brand === cleanBrand && method.last4 === cleanLast4)

  if (duplicate) {
    return {
      ok: false,
      error: `${cleanBrand} ending ${cleanLast4} is already saved.`,
      payload: null,
    }
  }

  return {
    ok: true,
    error: '',
    payload: { brand: cleanBrand, last4: cleanLast4 },
  }
}

export function getPaymentVerificationState(account) {
  const methods = account?.savedPaymentMethods || []
  const primaryMethod = methods.find((method) => method.primary) || methods[0]
  const verified = Boolean(account?.verifiedBidder && primaryMethod)
  const setupComplete = Boolean(primaryMethod)

  return {
    bidderStatusLabel: verified ? 'Verified bidder' : setupComplete ? 'Card saved, verification syncing' : 'Verification required',
    methodCountLabel: `${methods.length} saved ${methods.length === 1 ? 'method' : 'methods'}`,
    methods,
    primaryMethod,
    setupComplete,
    verified,
  }
}

export function getPaymentSetupSteps(account) {
  const { primaryMethod, verified } = getPaymentVerificationState(account)

  return [
    {
      id: 'account',
      title: 'Account access',
      detail: 'Signed in collector account',
      complete: Boolean(account?.id || account?.email),
    },
    {
      id: 'card',
      title: 'Primary card',
      detail: primaryMethod ? `${primaryMethod.brand} ending ${primaryMethod.last4}` : 'Add one secure card',
      complete: Boolean(primaryMethod),
    },
    {
      id: 'bidder',
      title: 'Bidder verification',
      detail: verified ? 'Auction bidding enabled' : 'Required before placing bids',
      complete: verified,
    },
  ]
}

export function getPaymentProviderDetails(paymentProvider) {
  if (paymentProvider === 'stripe') {
    return {
      mode: 'stripe',
      title: 'Secure Stripe card setup',
      description: 'Stripe verifies and tokenizes the card. OBC stores only safe card metadata for bidder verification and checkout readiness.',
      action: 'Start secure setup',
      securityNote: 'Card numbers are handled by Stripe, not stored on the OBC server.',
    }
  }

  return {
    mode: 'internal-dev',
    title: 'Internal dev card setup',
    description: 'Stripe is not enabled on this server. Use this local-only flow to test bidder verification with card brand and last four digits.',
    action: 'Save internal dev card',
    securityNote: 'Use this only for local testing. Production should use Stripe setup.',
  }
}

export function getPaymentRemovalImpact(methods = [], methodId) {
  const method = methods.find((item) => item.id === methodId)

  if (!method) {
    return {
      canRemove: false,
      code: 'missing-method',
      message: 'Payment method was not found.',
    }
  }

  if (methods.length === 1) {
    return {
      canRemove: true,
      code: 'last-card',
      message: 'Removing this card turns off bidder verification.',
    }
  }

  if (method.primary) {
    return {
      canRemove: true,
      code: 'primary-card',
      message: 'Another saved card becomes primary after removal.',
    }
  }

  return {
    canRemove: true,
    code: 'backup-card',
    message: 'Removing this backup card keeps bidder verification active.',
  }
}
